import { request as lunaRequest } from '@luna/api';
import { isDefined, isString, mergeHeaders } from '@luna/utils';

import type { ApiRequestOptions } from './ApiRequestOptions';
import type { ClientOptions } from './ClientOptions';

const isBlob = (value: any): value is Blob => {
    return (
        typeof value === 'object' &&
        typeof value.type === 'string' &&
        typeof value.stream === 'function' &&
        typeof value.arrayBuffer === 'function' &&
        typeof value.constructor === 'function' &&
        typeof value.constructor.name === 'string' &&
        /^(Blob|File)$/.test(value.constructor.name) &&
        /^(Blob|File)$/.test(value[Symbol.toStringTag])
    );
};

const isFormData = (value: unknown): value is FormData => {
    return value instanceof FormData;
};

const getQueryString = (params: Record<string, unknown>): string => {
    const qs: string[] = [];

    const append = (key: string, value: any) => {
        qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    };

    const process = (key: string, value: any) => {
        if (isDefined(value)) {
            if (Array.isArray(value)) {
                value.forEach((v) => {
                    process(key, v);
                });
            } else if (typeof value === 'object') {
                Object.entries(value).forEach(([k, v]) => {
                    process(`${key}[${k}]`, v);
                });
            } else {
                append(key, value);
            }
        }
    };

    Object.entries(params).forEach(([key, value]) => {
        process(key, value);
    });

    if (qs.length > 0) {
        return `?${qs.join('&')}`;
    }

    return '';
};

const getUrl = (config: ClientOptions, options: ApiRequestOptions): string => {
    const path = options.url.replace(/{(.*?)}/g, (substring: string, group: string) => {
        // eslint-disable-next-line no-prototype-builtins
        if (options.path?.hasOwnProperty(group)) {
            return encodeURI(String(options.path[group]));
        }
        return substring;
    });

    // Ensure there is exactly one slash between baseUrl and path, since config.baseUrl may
    // have a trailing slash (e.g. "https://.../ebs/api/") and path always starts with one
    // (e.g. "/my/messenger/digest"), which would otherwise cause a double slash in the URL.
    const url = `${config.baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
    if (options.query) {
        return `${url}${getQueryString(options.query)}`;
    }
    return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
    if (options.formData) {
        const formData = new FormData();

        const process = (key: string, value: any) => {
            if (isString(value) || isBlob(value)) {
                formData.append(key, value);
            } else {
                formData.append(key, JSON.stringify(value));
            }
        };

        Object.entries(options.formData)
            .filter(([__, value]) => isDefined(value))
            .forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((v) => process(key, v));
                } else {
                    process(key, value);
                }
            });

        return formData;
    }
    return undefined;
};

const getHeaders = (config: ClientOptions, options: ApiRequestOptions): Headers => {
    const additionalHeaders = config.headers;

    const headers = Object.entries(
        mergeHeaders({ Accept: '*/*' }, additionalHeaders, options.headers),
    )
        .filter(([__, value]) => isDefined(value))
        .reduce(
            (accHeaders, [key, value]) => ({
                ...accHeaders,
                [key]: String(value),
            }),
            {} as Record<string, string>,
        );

    if (options.body) {
        if (options.mediaType) {
            headers['content-type'] = options.mediaType;
        } else if (isBlob(options.body)) {
            headers['content-type'] = options.body.type || 'application/octet-stream';
        } else if (isString(options.body)) {
            headers['content-type'] = 'text/plain';
        } else if (!isFormData(options.body)) {
            headers['content-type'] = 'application/json';
        }
    }

    return new Headers(headers);
};

const getRequestBody = (options: ApiRequestOptions): any => {
    if (options.body !== undefined) {
        if (options.mediaType?.includes('/json')) {
            return JSON.stringify(options.body);
        }
        if (isString(options.body) || isBlob(options.body) || isFormData(options.body)) {
            return options.body;
        }
        return JSON.stringify(options.body);
    }
    return undefined;
};

export const sendRequest = async <T>(
    config: ClientOptions,
    options: ApiRequestOptions,
    url: string,
    body: any,
    formData: FormData | undefined,
    headers: Headers,
) => {
    const request = {
        headers: Object.fromEntries(headers.entries()),
        body: body ?? formData,
        method: options.method,
        skipParseJSON: config.skipParseJSON ?? false,
        signal: config.signal ?? null,
    } as const;

    return lunaRequest<T>(url, request);
};

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @returns Promise<T>
 */
export const apiClientRequest = <T>(config: ClientOptions, options: ApiRequestOptions) => {
    const url = getUrl(config, options);
    const formData = getFormData(options);
    const body = getRequestBody(options);
    const headers = getHeaders(config, options);
    return sendRequest<T>(config, options, url, body, formData, headers);
};
